/* Smart Film PDP for Shopify: pricing by glass area, three buying paths, sticky bar, FAQ search.
   Pricing comes from the product's variants: variant N (in product order) is "N extra 0.1 m² steps".
   The order buttons submit the theme's real product form (multipart), so the window photo uploads
   as a line-item property and the cart and checkout stay Shopify's own. */
(() => {
  'use strict';
  const P = document.currentScript?.dataset.classPrefix ?? 'sf-';
  const q = selector => selector.replace(/\.(?=[a-z])/g, '.' + P);
  const cls = name => P + name;
  const $ = id => document.getElementById(id);
  const configNode = $('sf-config');
  if (!configNode || document.documentElement.dataset.smartFilmInitialized === 'true') return;
  document.documentElement.dataset.smartFilmInitialized = 'true';

  const diagnostics = [];
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (cause) { diagnostics.push('Bad #sf-config JSON'); return; }
  const variants = (config.variants || []).filter(v => Number.isFinite(v.id) && Number.isFinite(v.price));
  const base = Number(config.baseAreaM2) || 0.75;
  const stepArea = Number(config.stepAreaM2) || 0.1;
  const maxPiece = Number(config.maxSinglePieceCm) || 180;
  const tiers = config.tiers || {};
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!variants.length) diagnostics.push('Product has no variants for the area steps');

  /* ---------- Events: Shopify customer events (Web Pixels) + an in-page log ---------- */
  if (!Array.isArray(window.SMART_FILM_EVENTS)) window.SMART_FILM_EVENTS = [];
  function track(name, data = {}) {
    window.SMART_FILM_EVENTS.push({ name, data, timestamp: new Date().toISOString() });
    try { window.Shopify?.analytics?.publish?.(`smartfilm_${name}`, data); } catch (_) { /* analytics is optional */ }
  }

  /* ---------- Pricing ---------- */
  function parseMeasurement(raw, unit) {
    if (typeof raw !== 'string' || !['in', 'cm'].includes(unit)) return NaN;
    const text = raw.trim();
    if (!text || text.length > 64) return NaN;
    let value;
    if (/^\d+(?:[.,]\d+)?$/.test(text)) value = Number(text.replace(',', '.'));
    else if (unit === 'in') {
      const f = text.match(/^(?:(\d+)\s+)?(\d+)\/(\d+)$/);
      if (!f || Number(f[3]) <= 0 || Number(f[2]) >= Number(f[3])) return NaN;
      value = Number(f[1] || 0) + Number(f[2]) / Number(f[3]);
    } else return NaN;
    return Number.isFinite(value) && value > 0 ? value : NaN;
  }
  function ceilStable(value) {
    const nearest = Math.round(value);
    return Math.abs(value - nearest) <= Number.EPSILON * 32 * Math.max(1, Math.abs(value)) ? nearest : Math.ceil(value);
  }
  function stepFor(areaM2) { return Math.max(0, ceilStable((areaM2 - base) / stepArea)); }
  function calculateQuote(widthCm, heightCm, unit = 'in') {
    if (![widthCm, heightCm].every(v => Number.isFinite(v) && v > 0)) return { ok: false, reason: 'measurement' };
    const areaM2 = widthCm * heightCm / 10000;
    if (Math.min(widthCm, heightCm) > maxPiece + 1e-9) return { ok: false, reason: 'panels', areaM2 };
    const step = stepFor(areaM2);
    const variant = variants[step];
    if (!variant) return { ok: false, reason: 'project-check', areaM2 };
    if (variant.available === false) return { ok: false, reason: 'unavailable', areaM2 };
    return { ok: true, kind: 'measured', widthCm, heightCm, unit, areaM2, step, variant, price: variant.price };
  }
  function tierQuote(name) {
    const tier = tiers[name];
    const variant = tier && variants[tier.step];
    return variant ? { ok: true, kind: 'tier', tier: name, step: tier.step, variant, price: variant.price, label: tier.label, note: tier.note } : null;
  }
  const currency = config.currency || 'USD';
  const money = cents => new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  const split = cents => new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(cents / 100 / (config.installments || 4));
  const fmt = (v, d = 4) => new Intl.NumberFormat('en-US', { maximumFractionDigits: d, useGrouping: false }).format(v);
  const areaText = m2 => `${fmt(m2, 2)} m² · ${fmt(m2 * 10.7639, 1)} sq ft`;
  const sizeText = qo => `${fmt(qo.widthCm / (qo.unit === 'in' ? 2.54 : 1))} × ${fmt(qo.heightCm / (qo.unit === 'in' ? 2.54 : 1))} ${qo.unit}`;
  const api = { parseMeasurement, calculateQuote, tierQuote, diagnostics, config };
  window.SMART_FILM_APP = api;

  const setText = (id, text) => { const n = $(id); if (n) n.textContent = text; };
  const say = text => setText('live-status', text);
  const state = { unit: config.defaultUnit === 'cm' ? 'cm' : 'in', colour: 'white', path: 'size', tier: 'small', slide: 0,
    m: { width: { cm: null, raw: '' }, height: { cm: null, raw: '' } }, quote: null, lastQuote: '', sceneMode: 'clear', sceneTouched: false, source: 'tape' };

  /* ---------- Dialogs ---------- */
  const returnFocus = new WeakMap();
  function closeDialog(dialog, restore = true) { if (!dialog?.open) return; if (!restore) returnFocus.delete(dialog); dialog.close(); }
  function openDialog(dialog, opener = document.activeElement) {
    if (!dialog || dialog.open) return false;
    document.querySelectorAll('dialog[open]').forEach(d => closeDialog(d, false));
    returnFocus.set(dialog, opener);
    try { dialog.showModal(); return true; } catch (cause) { diagnostics.push(`Unable to open #${dialog.id}`); return false; }
  }
  document.querySelectorAll('.sf dialog').forEach(dialog => {
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) closeDialog(dialog);
    });
    dialog.addEventListener('close', () => {
      if (dialog.id === 'video-dialog') $('source-video')?.removeAttribute('src');
      updateSticky();
      const target = returnFocus.get(dialog);
      returnFocus.delete(dialog);
      if (!document.querySelector('dialog[open]') && target?.isConnected && !target.disabled) target.focus();
    });
    dialog.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', () => closeDialog(dialog)));
  });
  api.openDialog = openDialog; api.closeDialog = closeDialog;

  /* ---------- Paths ---------- */
  const paths = [...document.querySelectorAll(q('.path'))];
  function openPath(name, { focus = false, scroll = false } = {}) {
    if (!paths.some(p => p.dataset.path === name)) return;
    state.path = name;
    paths.forEach(path => {
      const open = path.dataset.path === name;
      path.dataset.open = String(open);
      path.querySelector(q('.path-head'))?.setAttribute('aria-expanded', String(open));
      const body = path.querySelector(q('.path-body'));
      if (body) body.hidden = !open;
    });
    track('path_open', { path: name });
    const target = document.querySelector(`[data-path="${name}"]`);
    if (scroll) target?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (focus) (name === 'size' ? $('width') : name === 'later' ? document.querySelector('input[name="sf-tier"]:checked') : $('lead-name'))?.focus({ preventScroll: true });
    renderLater(); updateSticky(); observeOrderButton();
  }
  paths.forEach(path => path.querySelector(q('.path-head'))?.addEventListener('click', () => {
    if (path.dataset.path === 'help') setHelpReason('measure');
    openPath(path.dataset.path);
  }));
  function moveTo(path = 'size') {
    document.querySelectorAll('dialog[open]').forEach(d => closeDialog(d, false));
    openPath(path, { focus: true, scroll: true });
  }
  document.querySelectorAll('[data-size-link]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); moveTo('size'); }));
  document.querySelectorAll('[data-open-path]').forEach(b => b.addEventListener('click', e => {
    e.preventDefault();
    if (b.dataset.openPath === 'help') setHelpReason('measure');
    moveTo(b.dataset.openPath);
  }));

  /* ---------- Size path ---------- */
  function error(message = '', field = '') {
    setText('size-error', message);
    if ($('size-error')) $('size-error').hidden = !message;
    for (const n of ['width', 'height']) $(n)?.setAttribute('aria-invalid', String(n === field));
  }
  function capture(name) {
    const field = $(name), m = state.m[name];
    if (!field || field.value === m.raw) return;
    const v = parseMeasurement(field.value, state.unit) * (state.unit === 'in' ? 2.54 : 1);
    m.cm = Number.isFinite(v) ? v : null; m.raw = field.value;
  }
  function drawPreview(w, h) {
    const svg = $('wp-svg'); if (!svg) return;
    const ready = w > 0 && h > 0, W = ready ? w : 90, H = ready ? h : 110;
    const s = Math.min(140 / W, 112 / H), gw = Math.max(8, W * s), gh = Math.max(8, H * s), x = 110 - gw / 2, y = 84 - gh / 2;
    const frame = $('wp-frame'), glass = $('wp-glass');
    [['x', x - 5], ['y', y - 5], ['width', gw + 10], ['height', gh + 10]].forEach(([k, v]) => frame.setAttribute(k, v));
    [['x', x], ['y', y], ['width', gw], ['height', gh]].forEach(([k, v]) => glass.setAttribute(k, v));
    const d = state.unit === 'in' ? 2.54 : 1;
    $('wp-w').setAttribute('y', y - 10); $('wp-w').textContent = ready ? `${fmt(w / d, 2)} ${state.unit}` : 'width';
    $('wp-h').setAttribute('x', x - 13); $('wp-h').setAttribute('transform', `rotate(-90 ${x - 13} 84)`);
    $('wp-h').textContent = ready ? `${fmt(h / d, 2)} ${state.unit}` : 'height';
    $('window-preview').dataset.ready = String(ready);
  }
  function resetQuote(note = config.text?.updates || 'Updates as you type') {
    state.quote = null; state.lastQuote = '';
    setText('quote-price', '—'); setText('quote-meta', note);
    setText('order-size-label', config.text?.enterSize || 'Enter your size to see your price');
    if ($('order-size-price')) $('order-size-price').hidden = true;
    if ($('order-size')) { $('order-size').disabled = true; delete $('order-size').dataset.route; }
    drawPreview(state.m.width.cm, state.m.height.cm); setText('wp-note', 'Your window, drawn to scale');
    if ($('unit-hint')) $('unit-hint').hidden = true;
    updateCost(); updateSticky();
    sizeConfirm(false);
  }
  function syncQuote({ emit = true } = {}) {
    for (const n of ['width', 'height']) capture(n);
    for (const n of ['width', 'height']) {
      if (($(n)?.value.trim() || '') && state.m[n].cm === null) {
        resetQuote(); error(`Enter the ${n} as a number${state.unit === 'in' ? ', like 24 or 24 1/2' : ', like 61'}.`, n); return null;
      }
    }
    if (['width', 'height'].some(n => state.m[n].cm === null)) { resetQuote(); error(); return null; }
    const quote = calculateQuote(state.m.width.cm, state.m.height.cm, state.unit);
    if (!quote.ok) {
      resetQuote('Bigger than one piece of film');
      if (['panels', 'project-check'].includes(quote.reason)) {
        error(quote.reason === 'panels'
          ? `Both sides are over ${fmt(maxPiece / 100, 1)} m (${Math.round(maxPiece / 2.54)} in), so this needs two pieces side by side. Get a free quote below.`
          : 'That is a big project. We plan it with you: get a free quote below.');
        setText('order-size-label', 'Get a free quote');
        if ($('order-size-price')) $('order-size-price').hidden = true;
        $('order-size').disabled = false; $('order-size').dataset.route = 'quote';
        unitHint(state.m.width.cm, state.m.height.cm);
      } else error('This size is not available to order online. Get a free quote below.');
      return null;
    }
    state.quote = quote; error();
    const box = document.querySelector(q('.your-price'));
    if ($('quote-price').textContent !== money(quote.price) && box) { box.classList.remove(cls('bump')); void box.offsetWidth; box.classList.add(cls('bump')); }
    setText('quote-price', money(quote.price));
    setText('quote-meta', `${areaText(quote.areaM2)} · or 4 payments of ${split(quote.price)}`);
    setText('order-size-label', config.text?.orderSize || 'Order My Smart Window');
    setText('order-size-price', money(quote.price));
    if ($('order-size-price')) $('order-size-price').hidden = false;
    $('order-size').disabled = false; delete $('order-size').dataset.route;
    sizeConfirm(true);
    drawPreview(quote.widthCm, quote.heightCm);
    unitHint(quote.widthCm, quote.heightCm);
    const note = $('wp-note'); note.textContent = '';
    const b = document.createElement('b'); b.textContent = sizeText(quote);
    note.append(b, document.createTextNode(`Glass ${areaText(quote.areaM2)}`));
    updateCost(); updateSticky();
    const sig = `${quote.widthCm}|${quote.heightCm}|${quote.price}`;
    if (emit && sig !== state.lastQuote) { state.lastQuote = sig; track('quote_ready', { price: quote.price / 100, area_m2: +quote.areaM2.toFixed(4), currency }); say(`Your price is ${money(quote.price)}. We cut to the size you enter.`); }
    return quote;
  }
  function unitButtons() {
    for (const u of ['in', 'cm']) $('unit-' + u)?.setAttribute('aria-pressed', String(state.unit === u));
    document.querySelectorAll('[data-unit-label]').forEach(n => { n.textContent = state.unit; });
    if ($('width')) $('width').placeholder = state.unit === 'in' ? 'e.g. 24' : 'e.g. 61';
    if ($('height')) $('height').placeholder = state.unit === 'in' ? 'e.g. 48' : 'e.g. 122';
  }
  function changeUnit(next) {
    if (next === state.unit) return;
    for (const n of ['width', 'height']) { capture(n); if ($(n)?.value.trim() && state.m[n].cm === null) { error('Please finish this measurement before changing units.', n); return; } }
    state.unit = next;
    for (const n of ['width', 'height']) { const m = state.m[n]; m.raw = m.cm === null ? '' : fmt(m.cm / (next === 'in' ? 2.54 : 1)); $(n).value = m.raw; }
    unitButtons(); syncQuote();
  }
  ['width', 'height'].forEach(n => $(n)?.addEventListener('input', () => { uncheckSize(); state.source = 'tape'; syncQuote(); }));
  /* Size confirmation (boss 29/10: "phải bắt buộc khách đo cho chính xác", then "cho khách 1 cách để ballpark ... trả tiền trc. Rồi mình
     confirm lại bằng email"): once a price shows, the shopper must say how they measured (tape = exact; estimate = we email to confirm
     before cutting). New numbers clear the answer; a phone-camera size is pre-marked as an estimate. */
  function sizeChecks() { return ['size-tape', 'size-estimate']; }   // a function: resetQuote can run before this line
  function uncheckSize() { sizeChecks().forEach(id => { if ($(id)) $(id).checked = false; }); }
  function sizeConfirm(ok) {
    const box = $('size-confirm'); if (!box) return;
    box.hidden = !ok;
    if ($('sc-camera')) $('sc-camera').hidden = !(ok && state.source === 'camera');
    if (!ok) return;
    const done = sizeChecks().some(id => $(id)?.checked);
    box.dataset.done = String(done);
    $('order-size').disabled = !done;
    setText('order-size-label', done ? (config.text?.orderSize || 'Order My Smart Window') : (config.text?.confirmSize || 'Choose how you measured'));
    if ($('order-size-price')) $('order-size-price').hidden = !done;
  }
  sizeChecks().forEach(id => $(id)?.addEventListener('change', () => { sizeConfirm(Boolean(state.quote)); if (sizeChecks().some(x => $(x)?.checked)) say($('size-estimate')?.checked ? 'Estimate noted. We email you to confirm before we cut.' : 'Tape size noted. You can order now.'); }));

  $('unit-in')?.addEventListener('click', () => changeUnit('in'));
  $('unit-cm')?.addEventListener('click', () => changeUnit('cm'));
  /* Unit slip guard (owner 29/10, #119: the size step must be "dumb people friendly"): numbers that only make sense in the
     other unit get a one-tap fix that keeps the typed numbers and switches the unit. It never blocks the order. */
  function unitHint(w, h) {
    const box = $('unit-hint'); if (!box) return;
    const big = Math.max(w, h);
    const to = state.unit === 'cm' && big < 30 ? 'in' : state.unit === 'in' && big > 254 ? 'cm' : '';
    box.hidden = !to; if (!to) return;
    const typed = `${state.m.width.raw.trim()} × ${state.m.height.raw.trim()}`;
    box.querySelector('[data-unit-hint-text]').textContent = to === 'in'
      ? `${typed} cm is smaller than a sheet of paper. Did you measure in inches?`
      : `${typed} in is over 8 feet. Did you measure in centimetres?`;
    const btn = box.querySelector('[data-unit-swap]'); btn.dataset.to = to; btn.textContent = `Yes, it's ${typed} ${to}`;
  }
  $('unit-hint')?.querySelector('[data-unit-swap]')?.addEventListener('click', e => {
    const to = e.currentTarget.dataset.to; if (!['in', 'cm'].includes(to)) return;
    state.unit = to;
    for (const n of ['width', 'height']) { state.m[n].raw = null; capture(n); }
    unitButtons(); syncQuote();
    track('unit_fix', { to });   // how often shoppers type the wrong unit (smartfilm_unit_fix)
  });
  api.setMeasurementsCm = (w, h, source = 'camera') => {
    if (![w, h].every(v => Number.isFinite(v) && v > 0)) return false;
    openPath('size');
    const d = state.unit === 'in' ? 2.54 : 1, r = v => state.unit === 'in' ? Math.round(v / d * 8) / 8 : Math.round(v / d * 10) / 10;
    $('width').value = fmt(r(w), 3); $('height').value = fmt(r(h), 3);
    uncheckSize(); state.source = source;
    if (source === 'camera' && $('size-estimate')) $('size-estimate').checked = true;
    syncQuote();
    track('camera_measure', { width_cm: +w.toFixed(1), height_cm: +h.toFixed(1) });
    return true;
  };

  /* ---------- Measure later ---------- */
  function renderLater() {
    const t = tierQuote(state.tier);
    if (!t) return;
    setText('later-price', money(t.price)); setText('later-meta', `or 4 payments of ${split(t.price)}`); setText('order-later-price', money(t.price));
  }
  document.querySelectorAll('input[name="sf-tier"]').forEach(i => i.addEventListener('change', () => { if (i.checked) { state.tier = i.value; renderLater(); updateSticky(); } }));
  function setHelpReason(reason) {
    const quote = reason === 'quote';
    if ($('help-reason')) $('help-reason').value = quote ? 'Free quote (bigger window)' : 'Help me measure';
    setText('help-intro-copy', quote ? config.text?.quoteIntro : config.text?.helpIntro);
    setText('help-submit', quote ? (config.text?.quoteButton || 'Get My Free Quote') : (config.text?.helpButton || 'Help Me Measure'));
  }
  $('get-quote')?.addEventListener('click', () => { setHelpReason('quote'); openPath('help', { focus: true, scroll: true }); });

  /* ---------- Submit to the real Shopify cart ---------- */
  const form = $('sf-product-form');
  const photoInput = $('window-photo');
  const prop = name => form?.querySelector(`[data-prop="${name}"]`);
  function setProp(name, value) { const el = prop(name); if (!el) return; el.disabled = value === null || value === undefined || value === ''; el.value = el.disabled ? '' : value; }
  function prepare(kind) {
    const colour = config.colours?.[state.colour]?.label || state.colour;
    setProp('colour', colour);
    if (kind === 'measured') {
      const qo = syncQuote({ emit: false });
      if (!qo) return null;
      $('sf-variant-id').value = qo.variant.id;
      setProp('width', `${fmt(qo.widthCm / (qo.unit === 'in' ? 2.54 : 1))} ${qo.unit}`);
      setProp('height', `${fmt(qo.heightCm / (qo.unit === 'in' ? 2.54 : 1))} ${qo.unit}`);
      setProp('area', `${fmt(qo.areaM2, 3)} m²`);
      setProp('size', null); setProp('due', null);
      setProp('measured', state.source === 'camera' ? 'Phone camera estimate' : 'Customer measurement');
      setProp('check', document.getElementById('size-tape')?.checked ? 'Tape measure (exact)' : document.getElementById('size-estimate')?.checked ? 'Estimate: confirm the exact size by email before cutting' : null);
      return qo;
    }
    const t = tierQuote(state.tier);
    if (!t) return null;
    $('sf-variant-id').value = t.variant.id;
    ['width', 'height', 'area', 'measured', 'check'].forEach(n => setProp(n, null));
    setProp('size', `Measure later: ${t.label} (${t.note})`);
    setProp('due', 'Within 14 days');
    return t;
  }
  let submitting = false;
  // Window capture runs before the theme's cart drawer (a document capture listener that reads the form into /cart/add.js and
  // skips events already prevented): the size properties must be filled in before anyone builds the form data.
  window.addEventListener('submit', event => {
    if (!form || event.target !== form) return;
    if (submitting) { event.preventDefault(); return; }
    const kind = event.submitter?.id === 'order-later' || (!event.submitter && state.path === 'later') ? 'tier' : 'measured';
    if (kind === 'measured' && $('order-size')?.dataset.route === 'quote') { event.preventDefault(); setHelpReason('quote'); openPath('help', { focus: true, scroll: true }); return; }
    const item = prepare(kind);
    if (!item) { event.preventDefault(); moveTo('size'); return; }
    // An empty file input would post an empty "Window photo" line-item property.
    if (photoInput) photoInput.disabled = !photoInput.files?.length;
    submitting = true;
    track('add_to_cart', { kind: item.kind, price: item.price / 100, currency, variant_id: item.variant.id });
    // Next: the cart drawer posts the form to /cart/add.js and opens, or the native multipart POST goes to /cart. Both read the
    // form data synchronously, so the page can be made usable again right after (the drawer path never reloads the page).
    setTimeout(() => { if (photoInput) photoInput.disabled = false; }, 0);
    setTimeout(() => { submitting = false; }, 2500);
  }, true);
  window.addEventListener('pageshow', () => { submitting = false; if (photoInput) photoInput.disabled = false; });

  /* ---------- Help: Shopify contact form ---------- */
  $('help-form')?.addEventListener('submit', event => {
    const name = $('lead-name').value.trim(), digits = $('lead-phone').value.replace(/\D/g, ''), email = $('lead-email');
    let message = '';
    if (!name) message = 'Please add your name.';
    else if (digits.length < 7 || digits.length > 15) message = 'Please add a phone number we can text or WhatsApp.';
    else if (email && email.required && !/^\S+@\S+\.\S+$/.test(email.value)) message = 'Please add your email so we can send the details.';
    if (message) { event.preventDefault(); setText('lead-error', message); $('lead-error').hidden = false; return; }
    const type = document.querySelector('input[name="contact[Window type]"]:checked')?.value || 'Other';
    if (config.leadMode === 'whatsapp' && config.whatsapp) {
      event.preventDefault();
      const text = `Hi, I'm ${name}. ${$('help-reason')?.value || 'Help me measure'} for my ${type.toLowerCase()} window. My number is ${$('lead-phone').value.trim()}. I'll send a photo here.`;
      window.open(`https://wa.me/${config.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
      track('lead_submit', { reason: $('help-reason')?.value, window_type: type, channel: 'whatsapp' });
      return;
    }
    const body = $('lead-body');
    if (body) body.value = `${$('help-reason')?.value || 'Help me measure'} · Window: ${type} · Phone: ${$('lead-phone').value.trim()} · Messages OK: ${$('lead-consent')?.checked ? 'yes' : 'no'}`;
    track('lead_submit', { reason: $('help-reason')?.value, window_type: type });
  });

  /* ---------- Colour ---------- */
  // Only the swatch buttons: the hero scene also carries data-colour, and a tap on it must not re-select white.
  const swatches = [...document.querySelectorAll(q('.swatches [data-colour]'))];
  function selectColour(colour, { emit = true } = {}) {
    if (!config.colours?.[colour] || config.colours[colour].available === false) return;
    state.colour = colour;
    swatches.forEach(b => { const on = b.dataset.colour === colour; b.setAttribute('aria-checked', String(on)); b.tabIndex = on ? 0 : -1; });
    setText('colour-label', config.colours[colour].label);
    if ($('hero-scene')) $('hero-scene').dataset.colour = colour;
    if (emit) {
      track('colour_select', { colour }); goToSlide(0, false); setScene('private', { user: true, silent: true });
      // owner 30/10: bring the picture into view so the shopper sees the colour (phones: the gallery is above the buy box)
      const gal = document.querySelector('.sf-gallery'), r = gal?.getBoundingClientRect();
      if (r && (r.top < 0 || r.bottom > innerHeight)) scrollTo({ top: Math.max(0, r.top + scrollY - 72), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  }
  swatches.forEach(b => {
    b.addEventListener('click', () => selectColour(b.dataset.colour));
    b.addEventListener('keydown', e => {
      if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const on = swatches.filter(x => !x.disabled), i = on.indexOf(b);
      const next = on[(i + (['ArrowRight', 'ArrowDown'].includes(e.key) ? 1 : -1) + on.length) % on.length];
      next.focus(); selectColour(next.dataset.colour);
    });
  });

  /* ---------- Gallery + hero switch ---------- */
  const slides = [...document.querySelectorAll(q('.slide'))];
  const thumbs = [...document.querySelectorAll('[data-thumb]')];
  function goToSlide(index, emit = true) {
    if (!slides.length) return;
    const next = ((index % slides.length) + slides.length) % slides.length;
    const changed = next !== state.slide;
    state.slide = next;
    $('gallery-track').style.transform = `translateX(${-100 * next}%)`;
    slides.forEach((s, i) => { s.setAttribute('aria-hidden', String(i !== next)); s.inert = i !== next; });
    thumbs.forEach((t, i) => t.setAttribute('aria-pressed', String(i === next)));
    // Keep the active thumbnail in view by scrolling the strip itself: scrollIntoView would also scroll the page.
    const strip = thumbs[next]?.parentElement;
    if (strip && strip.scrollWidth > strip.clientWidth) {
      const t = thumbs[next];
      strip.scrollLeft = Math.max(0, t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2);
    }
    setText('gallery-count', `${next + 1} / ${slides.length}`);
    slides[next].querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
    if (emit && changed) track('gallery_change', { slide: next + 1 });
  }
  thumbs.forEach(t => t.addEventListener('click', () => goToSlide(Number(t.dataset.thumb))));
  $('gallery-viewport')?.addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goToSlide(state.slide + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); goToSlide(state.slide - 1); }
  });
  (() => {
    const vp = $('gallery-viewport'); if (!vp) return;
    let x0 = null, y0 = null, dx = 0, swiping = false, id = null;
    vp.addEventListener('pointerdown', e => { if (e.pointerType === 'mouse' && e.button !== 0) return; x0 = e.clientX; y0 = e.clientY; dx = 0; swiping = false; id = e.pointerId; });
    vp.addEventListener('pointermove', e => {
      if (x0 === null || e.pointerId !== id) return;
      dx = e.clientX - x0;
      if (!swiping && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(e.clientY - y0)) { swiping = true; vp.setPointerCapture?.(id); }
      if (swiping) $('gallery-track').style.transform = `translateX(calc(${-100 * state.slide}% + ${dx}px))`;
    });
    const end = () => { if (x0 === null) return; if (swiping) { vp.dataset.swiped = 'true'; setTimeout(() => delete vp.dataset.swiped, 50); goToSlide(state.slide + (dx < -50 ? 1 : dx > 50 ? -1 : 0)); } x0 = null; swiping = false; };
    vp.addEventListener('pointerup', end); vp.addEventListener('pointercancel', end);
  })();
  function setScene(mode, { user = false, silent = false } = {}) {
    const scene = $('hero-scene'); if (!scene) return;
    state.sceneMode = mode; scene.dataset.mode = mode; scene.setAttribute('aria-pressed', String(mode === 'private'));
    if (user) { scene.classList.add(cls('touched')); state.sceneTouched = true; }
    document.querySelectorAll('[data-scene-mode]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.sceneMode === mode)));
    if (user && !silent) track('scene_switch', { mode });
  }
  $('hero-scene')?.addEventListener('click', () => { if (!$('gallery-viewport')?.dataset.swiped) setScene(state.sceneMode === 'clear' ? 'private' : 'clear', { user: true }); });
  // The remote sits under the gallery: pressing it brings the live slide back into view first.
  document.querySelectorAll('[data-scene-mode]').forEach(b => b.addEventListener('click', () => {
    if (state.slide !== 0) goToSlide(0);
    setScene(b.dataset.sceneMode, { user: true });
  }));
  (() => {
    const scene = $('hero-scene'); if (!scene) return;
    let visible = true;
    new IntersectionObserver(e => { visible = e[0]?.isIntersecting === true; }, { threshold: 0.3 }).observe(scene);
    const timer = setInterval(() => {
      if (document.documentElement.dataset.heroVideo === 'on' || state.sceneTouched || reducedMotion() || !visible || state.slide !== 0 || document.hidden || document.querySelector('dialog[open]')) return;
      setScene(state.sceneMode === 'clear' ? 'private' : 'clear');
    }, 2600);
    addEventListener('pagehide', () => clearInterval(timer), { once: true });
  })();
  api.sceneState = () => ({ mode: state.sceneMode, touched: state.sceneTouched, slide: state.slide });

  /* ---------- Sticky bar ---------- */
  // pastPrice: the visitor has scrolled past the title and price, so the bar never covers them on arrival.
  let orderVisible = false, buyVisible = false, formFocused = false, finalVisible = false, orderObserver = null, pastPrice = !('IntersectionObserver' in window);
  const desktop = matchMedia('(min-width: 1024px)');
  if ($('buy')) new IntersectionObserver(e => { buyVisible = e[0]?.isIntersecting === true; updateSticky(); }).observe($('buy'));
  const priceLine = document.querySelector(q('.price-line'));
  if (priceLine && 'IntersectionObserver' in window) new IntersectionObserver(e => { pastPrice = !!e[0] && !e[0].isIntersecting && e[0].boundingClientRect.top < 0; updateSticky(); }).observe(priceLine);
  else pastPrice = true;
  // Phones only (overhaul v3): desktop has the price button in the chapter bar. The final offer has its own button.
  if ($('sf-final') && 'IntersectionObserver' in window) new IntersectionObserver(e => { finalVisible = e[0]?.isIntersecting === true; updateSticky(); }).observe($('sf-final'));
  desktop.addEventListener?.('change', () => updateSticky());
  document.querySelector(q('.buybox'))?.addEventListener('focusin', e => { if (e.target.matches('input')) { formFocused = true; updateSticky(); } });
  document.querySelector(q('.buybox'))?.addEventListener('focusout', () => { formFocused = false; setTimeout(updateSticky, 50); });
  function observeOrderButton() {
    orderObserver?.disconnect();
    const target = state.path === 'size' ? $('order-size') : state.path === 'later' ? $('order-later') : $('help-submit');
    if (!target) return;
    orderObserver = new IntersectionObserver(e => { orderVisible = e[0]?.isIntersecting === true; updateSticky(); }, { threshold: 0.5 });
    orderObserver.observe(target);
  }
  function updateSticky() {
    const bar = $('sticky-bar'); if (!bar) return;
    let label = config.text?.stickyLabel || 'Made-to-measure kit', price = $('from-price')?.textContent || '', action = 'Check my price';
    if (state.path === 'size' && state.quote) { label = sizeText(state.quote); price = money(state.quote.price); action = 'Order now'; }
    else if (state.path === 'later') { const t = tierQuote(state.tier); if (t) { label = `${t.label} kit`; price = money(t.price); action = 'Order now'; } }
    else if (state.path === 'help') { label = 'Measuring help'; price = 'Free'; action = 'Get help'; }
    setText('sticky-label', label); setText('sticky-price', price); setText('sticky-action-label', action);
    const show = !desktop.matches && pastPrice && !orderVisible && !formFocused && !finalVisible && !document.querySelector('dialog[open]');
    bar.hidden = false; bar.classList.toggle(cls('show'), show); bar.inert = !show; bar.setAttribute('aria-hidden', String(!show));
  }
  $('sticky-action')?.addEventListener('click', () => {
    if (state.path === 'size' && state.quote) form?.requestSubmit($('order-size'));
    else if (state.path === 'later') form?.requestSubmit($('order-later'));
    else moveTo(state.path);
  });

  /* ---------- Photo: the file input is named, so the browser uploads it with the form ---------- */
  function showPhoto(file) {
    const prev = $('photo-preview');
    if (prev?.src?.startsWith('blob:')) URL.revokeObjectURL(prev.src);
    if (!file) { $('photo-selected').hidden = true; setText('photo-status', ''); return; }
    if (file.size > (config.maxPhotoBytes || 20 * 1024 * 1024)) { photoInput.value = ''; setText('photo-status', 'Choose a photo up to 20 MB.'); return; }
    prev.src = URL.createObjectURL(file); $('photo-selected').hidden = false;
    setText('photo-name', file.name || 'Window photo'); setText('photo-status', 'Photo added. It is sent with your order.');
  }
  photoInput?.addEventListener('change', () => showPhoto(photoInput.files?.[0]));
  $('photo-remove')?.addEventListener('click', () => { photoInput.value = ''; showPhoto(null); });
  api.usePhoto = async file => {
    if (!photoInput || !file) return false;
    try { const dt = new DataTransfer(); dt.items.add(file); photoInput.files = dt.files; showPhoto(file); return true; } catch (_) { return false; }
  };

  /* ---------- Running cost ---------- */
  // The running-cost card belongs to sf-v3.js (the visitor's own rate, no default price per kWh).
  function updateCost() {}

  /* ---------- Guides, demo video ---------- */
  function guideTab(tab) {
    document.querySelectorAll('[data-guide-tab]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.guideTab === tab)));
    document.querySelectorAll('[data-guide-panel]').forEach(p => { p.hidden = p.dataset.guidePanel !== tab; });
  }
  document.querySelectorAll('[data-guide-tab]').forEach(b => b.addEventListener('click', () => guideTab(b.dataset.guideTab)));
  document.querySelectorAll('[data-guide]').forEach(b => b.addEventListener('click', () => { guideTab(b.dataset.guide); if (openDialog($('guide-dialog'), b)) track('guide_open', { topic: b.dataset.guide }); }));
  document.querySelectorAll('[data-demo-video]').forEach(b => b.addEventListener('click', () => {
    if (!config.demoVideoUrl) return;
    $('source-video').src = `${config.demoVideoUrl}${config.demoVideoUrl.includes('?') ? '&' : '?'}rel=0&autoplay=1`;
    openDialog($('video-dialog'), b);
  }));

  /* ---------- FAQ ---------- */
  const faqLayout = document.querySelector(q('.faq-layout'));
  const groups = [...document.querySelectorAll('[data-faq-group]')], panels = [...document.querySelectorAll('[data-faq-panel]')];
  function selectGroup(id) { if (id !== 'all' && !panels.some(p => p.dataset.faqPanel === id)) return; groups.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.faqGroup === id))); panels.forEach(p => { p.hidden = id !== 'all' && p.dataset.faqPanel !== id; }); }
  groups.forEach(b => b.addEventListener('click', () => selectGroup(b.dataset.faqGroup)));
  document.querySelectorAll('.sf details').forEach((d, i) => {
    let was = d.open;
    d.addEventListener('toggle', () => { if (d.open && !was) track('faq_open', { question: d.querySelector('summary')?.textContent.trim().slice(0, 120) || `item-${i}` }); was = d.open; });
  });
  let searchTimer;
  $('faq-q')?.addEventListener('input', () => {
    const query = $('faq-q').value.trim().toLowerCase();
    const items = [...document.querySelectorAll(q('.faq-panel details'))];
    faqLayout?.classList.toggle(cls('faq-searching'), !!query);
    let shown = 0;
    items.forEach(d => {
      const hay = `${d.closest('[data-faq-panel]')?.getAttribute('aria-label') || ''} ${d.textContent}`.toLowerCase();
      const match = !query || query.split(/\s+/).every(w => hay.includes(w));
      d.hidden = !match; if (query && match) shown++;
    });
    if (!query) { items.forEach(d => { d.hidden = false; }); selectGroup(groups.find(b => b.getAttribute('aria-pressed') === 'true')?.dataset.faqGroup || groups[0]?.dataset.faqGroup); }
    else panels.forEach(p => { p.hidden = ![...p.querySelectorAll('details')].some(d => !d.hidden); });
    setText('faq-count', query ? `${shown} found` : '');
    if ($('faq-empty')) $('faq-empty').hidden = !query || shown > 0;
    clearTimeout(searchTimer);
    if (query) searchTimer = setTimeout(() => track('faq_search', { query_length: query.length, results: shown }), 800);
  });

  /* ---------- Init ---------- */
  unitButtons();
  selectColour('white', { emit: false });
  goToSlide(0, false);
  setScene('clear');
  if (groups.length) selectGroup(groups[0].dataset.faqGroup);
  openPath(config.openPath || 'size');
  if (config.helpPosted) { setHelpReason('measure'); openPath('help'); }
  window.SMART_FILM_EVENTS.length = 0;
  syncQuote({ emit: false }); renderLater(); updateCost(); observeOrderButton(); updateSticky();
  if (diagnostics.length) console.warn('[Smart Film]', diagnostics.join(' '));
})();
